
import { Injectable } from '@angular/core';

import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

import { JournodexEnumsService } from '../../../Discover/Core/services/journodex-enums.service';
import { JournodexEntityFactory } from '../../../Discover/Targeting/factories/journodex-entity.factory';
import { OutletModel } from '../../../Discover/Targeting/models/outlet.model';
import { TargetingSearchFullModel } from '../../../Discover/Targeting/models/targeting-search-full.model';
import { TargetingSearch } from '../../../Discover/Targeting/models/targeting-search.model';
import { TargetingSearchesService } from '../../../Discover/Targeting/services/targeting-searches.service';

import { JournodexSubjectsService } from '../../Journodex/services/journodex-subjects.service';
import { IncludeExcludeModel } from '../../Misc/models/include-exclude-model';

import { ContactModel } from '../model/contact.model';
import { TargetingGeographyService } from './targeting-geography-service';


@Injectable()
export class TargetingSearchFormService {

    constructor(
        public journodexSubjectsService: JournodexSubjectsService,
        public targetingSearchesService: TargetingSearchesService,
        public journodexEnumsService: JournodexEnumsService,
        private journodexEntityFactory: JournodexEntityFactory,
        public targetingGeographyService: TargetingGeographyService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
    ) {}

    constructFullSearch(search: TargetingSearch): Observable<TargetingSearchFullModel> {
        const outlets: OutletModel[] = search?.outletIdsNames?.include.map(name => {
            const outlet = this.journodexEntityFactory.createEmptyOutlet();
            outlet.id = name.id;
            outlet.setName(name.name);
            return outlet;
        }) ?? [];

        const contacts: ContactModel[] = search?.contactIdsNames?.include.map(name => {
            const contact = this.journodexEntityFactory.createEmptyContact();
            contact.name = name.name;
            contact.id = name.id;
            return contact;
        }) ?? [];

        return forkJoin([
            this.includeExcludeFetcher(search.subjects, this.journodexSubjectsService.getByIds.bind(this.journodexSubjectsService)),
            this.includeExcludeFetcher(search.geographies, this.targetingGeographyService.getByGeographyIds.bind(this.targetingGeographyService), false),
            this.includeExcludeFetcher(search.jobRoles, this.journodexEnumsService.getJobRolesByIds.bind(this.journodexEnumsService)),
            this.includeExcludeFetcher<string>(search.languages, this.localizationService.getLanguagesById.bind(this.localizationService)),
            this.includeExcludeFetcher(search.outletSubjects, this.journodexSubjectsService.getByIds.bind(this.journodexSubjectsService)),
            this.includeExcludeFetcher(search.areasOfCoverage, this.targetingGeographyService.getByGeographyIds.bind(this.targetingGeographyService)),
            this.includeExcludeFetcher(search.mediaMarkets, this.journodexEnumsService.getMediaMarketsByIds.bind(this.journodexEnumsService)),
        ]).pipe(
            take(1),
            map(([subjects, geographies, jobRoles, languages, outletSubjects, areasOfCoverage, mediaMarkets]) => {
                return <TargetingSearchFullModel>{
                    name: search.name ?? null,
                    mediaTypes: search.mediaTypes,
                    productTypes: search.productTypes,
                    marketSectors: search.marketSectors,
                    coverageFocuses: search.coverageFocuses,
                    productFrequencies: search.productFrequencies,
                    targetAudiences: search.targetAudiences,
                    audienceReachType: search?.audienceReachFilter.type ?? null,
                    audienceReachMin: search.audienceReachFilter?.range?.min ?? null,
                    audienceReachMax: search.audienceReachFilter?.range?.max ?? null,
                    includePrivateContacts: search.includePrivateContacts,
                    groupByOutlet: search.groupByOutlet,

                    contacts: contacts,
                    keywords: search.monitoringKeywords,
                    subjects: subjects,
                    geographies: geographies,
                    jobTitles: search.jobTitles,
                    jobRoles: jobRoles,
                    languages: languages,

                    outlets: outlets,
                    outletKeywords: search.outletMonitoringKeywords,
                    outletSubjects: outletSubjects,
                    areasOfCoverage: areasOfCoverage,
                    mediaMarkets: mediaMarkets
                };
            })
        );
    }

    /** Converts a IncludeExcludeModel of ids to objects based on lookupById function */
    includeExcludeFetcher<T = number>(
        value: IncludeExcludeModel<T>,
        lookupById: Function,
        enableIncludeAll: boolean = true
    ): Observable<IncludeExcludeModel<any>> {
        if (value.isEmpty()) {
            return of(new IncludeExcludeModel());
        }

        if (enableIncludeAll) {
            return combineLatest([
                lookupById(value.includeAll),
                lookupById(value.include),
                lookupById(value.exclude),
            ]).pipe(
                take(1),
                map(([includeAll, include, exclude]: any[]) => {
                    return new IncludeExcludeModel({
                        includeAll: includeAll,
                        include: include,
                        exclude: exclude
                    });
                })
            );
        } else {
            return combineLatest([
                lookupById(value.include),
                lookupById(value.exclude),
            ]).pipe(
                take(1),
                map(([include, exclude]: any[]) => {
                    return new IncludeExcludeModel({
                        includeAll: [],
                        include: include,
                        exclude: exclude
                    });
                })
            );
        }
    }
}
